import _ from 'lodash';
import { isLosslessNumber, parse, stringify } from 'lossless-json';

import { removeLosslessObject } from './object';

export function calculateDifference(base: any, object: any): any {
  function changes(inBase: any, inObject: any) {
    if ((!inBase && inObject) || (inBase && !inObject)) return true;

    if (
      typeof inBase !== 'object' &&
      inBase != null &&
      !Array.isArray(inBase)
    ) {
      return inBase !== inObject;
    }

    if (isLosslessNumber(inBase)) {
      return !isLosslessNumber(inObject) || inBase.value !== inObject.value;
    }

    if (Array.isArray(inBase)) {
      if (!Array.isArray(inObject)) return true;

      if (
        typeof inBase[0] !== 'object' &&
        inBase[0] != null &&
        !Array.isArray(inBase[0])
      ) {
        const result = [];
        inBase.forEach((val) => {
          result.push(!inObject.includes(val));
        });

        return _.difference(inBase, inObject)?.length === inBase?.length
          ? true
          : result;
      }

      // if is array of object
      const result = [];
      for (let i = 0; i < inBase.length; i += 1) {
        result.push(changes(inBase[i], inObject[i]));
      }
    }

    const diff = {};

    if (inBase) {
      Object.keys(inBase).forEach((baseKey) => {
        if (
          !inObject ||
          !Object.prototype.hasOwnProperty.call(inObject, baseKey)
        ) {
          diff[baseKey] = true;
          return;
        }

        const res = changes(inBase?.[baseKey], inObject?.[baseKey]);

        if (typeof res !== 'object' && res != null && !Array.isArray(res)) {
          diff[baseKey] = res;
          return;
        }

        if (Object.keys(res)?.length > 0) diff[baseKey] = res;
      });
    }

    return diff;
  }

  return changes(base, object);
}

export function isFieldChanged(difference, pathSeparatedBySlash: string) {
  if (!pathSeparatedBySlash || !difference) return false;

  const paths = pathSeparatedBySlash.split('/');

  let value: null | boolean;

  paths.forEach((path: string) => {
    if (!value) {
      value = difference?.[path];

      return;
    }

    value = value?.[path];
  });

  return value;
}

function formatValue(
  inDiff,
  inObject,
  objToUpdate,
): { value; changed: boolean } {
  if (typeof inDiff === 'boolean') {
    if (!inDiff)
      return {
        value: inObject,
        changed: false,
      };

    if (inObject == null)
      return {
        value: `<span class='validation-json-modified'>${objToUpdate}</span>`,
        changed: true,
      };

    if (
      typeof inObject !== 'object' &&
      inObject != null &&
      !Array.isArray(inObject)
    ) {
      if (['number', 'boolean'].includes(typeof inObject)) {
        return {
          value: `<retain_format>${objToUpdate}</retain_format>`,
          changed: true,
        };
      }
      return {
        value: `<span class='validation-json-modified'>${objToUpdate}</span>`,
        changed: true,
      };
    }

    /** Special case for JSON Lossless number
     * LosslessNumber {isLosslessNumber: true, value: '959822372655852730'}
     * Treat as number with value 959822372655852730
     * */
    if (isLosslessNumber(inObject)) {
      return {
        value: `<retain_format>${inObject.value}</retain_format>`,
        changed: true,
      };
    }

    if (Array.isArray(inObject))
      return formatArray(inDiff, inObject, objToUpdate);

    return formatObject(inDiff, inObject, objToUpdate);
  }

  if (
    typeof inObject !== 'object' &&
    inObject != null &&
    !Array.isArray(inObject)
  ) {
    return formatValue(inDiff, inObject, objToUpdate);
  }

  if (Array.isArray(inObject))
    return formatArray(inDiff, inObject, objToUpdate);

  return formatObject(inDiff, inObject, objToUpdate);
}

function formatArray(
  inDiff: boolean | any[],
  inObject: any[],
  objToUpdate: any[],
): { value: any[]; changed: boolean } {
  const arr = [];

  let hasChanges: boolean = false;

  for (let i = 0; i < inObject.length; i += 1) {
    objToUpdate[i] = parse(stringify(inObject[i]));
    removeLosslessObject(objToUpdate[i]);

    const { value, changed } = formatValue(
      typeof inDiff === 'boolean' ? inDiff : inDiff[i],
      inObject[i],
      objToUpdate[i],
    );

    if (changed) {
      hasChanges = true;
      objToUpdate[i] = value;
    }

    arr.push(value);
  }

  return { value: arr, changed: hasChanges };
}

function formatObject(
  inDiff: boolean | { [key: string]: any },
  inObject: { [key: string]: any },
  objToUpdate: { [key: string]: any },
): { value; changed: boolean } {
  if (typeof inDiff === 'boolean' && Object.keys(inObject).length === 0) {
    return {
      value: `<span class='validation-json-modified'>{}</span>`,
      changed: true,
    };
  }

  let hasChanges: boolean = false;

  if (!inObject) return { value: inObject, changed: hasChanges };

  Object.keys(inObject).forEach((key) => {
    objToUpdate[key] = parse(stringify(inObject[key]));
    removeLosslessObject(objToUpdate[key]);

    const { value, changed } = formatValue(
      typeof inDiff === 'boolean' ? inDiff : inDiff?.[key],
      inObject[key],
      objToUpdate[key],
    );

    if (changed) {
      hasChanges = true;

      if (
        (typeof inDiff === 'boolean' && inDiff) ||
        (typeof inDiff?.[key] === 'boolean' && inDiff?.[key])
      ) {
        delete objToUpdate[key];

        objToUpdate[`<span class='validation-json-modified'>${key}</span>`] =
          value;

        return false;
      }

      objToUpdate[key] = value;
    }
  });

  return { value: objToUpdate, changed: hasChanges };
}

export function highlightDifference(value: string, diff): any {
  if (!diff || !Object.keys(diff)?.length) {
    let formattedValue = '';

    if (value) {
      const parsedValue = parse(value);
      removeLosslessObject(parsedValue);
      formattedValue = stringify(parsedValue, null, 2);
    }
    return `<span class='validation-json-modified'>${formattedValue}</span>`;
  }

  const parsed = parse(value);
  removeLosslessObject(parsed);
  const stringifiedValue = stringify(parsed);
  const valueClone = parse(stringifiedValue);
  removeLosslessObject(valueClone);

  let formattedResult = {};
  if (Array.isArray(valueClone)) formattedResult = [];
  formatValue(diff, valueClone, formattedResult);
  let formattedString = stringify(formattedResult, null, 2);

  // replace all <retain_format> with <span class='validation-json-modified'>
  formattedString = formattedString
    .replace(/"<retain_format>/g, "<span class='validation-json-modified'>")
    .replace(/<\/retain_format>"/g, '</span>');

  return formattedString;
}
